import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  InfoCircleOutlined,
  TableOutlined,
} from '@ant-design/icons';
import React, { FC, useState } from 'react';

import { useOperationsDashboardContext } from '../../contexts/OperationsDashboardContext';
import { DateRangeType } from '../../types/phlebotomist-dashboard';
import { computeFlagStatus, IHealthFlagRange } from '../../utils/healthFlags';
import styles from '../OperationsDashboard/ops-theme.module.css';
import OpsMetricDetailModal, {
  IOpsMetricDetailConfig,
} from '../OpsMetricDetailModal';

const PERIOD_LABELS: Partial<Record<DateRangeType, string>> = {
  yesterdayCal: 'Yesterday',
  lastWeekCal: 'Last Week',
  lastMonthCal: 'Last Month',
  lastQuarterCal: 'Last Quarter',
  pYearCal: 'Last Year',
  custom: 'Custom',
};

const TrendIndicator: FC<{
  current: number;
  previous: number | undefined;
  mode?: 'relative' | 'absolute';
}> = ({ current, previous, mode = 'relative' }) => {
  if (previous === undefined) return null;
  const delta = current - previous;
  if (Math.abs(delta) < 0.05) return null;
  const up = delta > 0;
  const display =
    mode === 'absolute'
      ? Math.abs(delta).toFixed(1)
      : (() => {
          if (previous === 0) return null;
          const raw = (delta / previous) * 100;
          return Math.abs(raw) < 1
            ? Math.abs(raw).toFixed(1)
            : `${Math.round(Math.abs(raw))}`;
        })();
  if (!display) return null;
  return (
    <span className={up ? styles.statusUp : styles.statusDown}>
      {up ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {display}%
    </span>
  );
};

export interface IOpsRecruiterSummaryCardsProps {
  activeDonorCount: number;
  historicalActiveDonorCount?: number;
  comparedPeriodActiveDonorCount?: number;
  activeDonorGrowthGoal?: IHealthFlagRange;
  recruitedCount: number;
  prevRecruitedCount?: number;
  unitsCollected: number;
  prevUnitsCollected?: number;
  daysInPeriod: number;
  prevDaysInPeriod?: number;
  successRate?: number;
  prevSuccessRate?: number;
  expectedPerDay: number;
  prevUnitsPerDay?: number;
  sglTotal: number;
  dblTotal: number;
  periodValue: DateRangeType;
  selectedRegionName?: string;
  loading?: boolean;
  metricsLoading?: boolean;
  onDrilldown?: (metricKey: string) => void;
  tooltips?: {
    activeDonors?: string;
    activeDonorsPeriod?: string;
    recruited?: string;
    unitsPerDay?: string;
    successRate?: string;
    sglPercent?: string;
  };
}

const OpsRecruiterSummaryCards: FC<IOpsRecruiterSummaryCardsProps> = ({
  activeDonorCount,
  historicalActiveDonorCount,
  comparedPeriodActiveDonorCount,
  activeDonorGrowthGoal,
  recruitedCount,
  prevRecruitedCount,
  unitsCollected,
  prevUnitsCollected,
  daysInPeriod,
  prevDaysInPeriod,
  successRate,
  prevSuccessRate,
  expectedPerDay,
  prevUnitsPerDay,
  sglTotal,
  dblTotal,
  periodValue,
  selectedRegionName,
  loading,
  metricsLoading,
  onDrilldown,
}) => {
  const { isDarkMode } = useOperationsDashboardContext();
  const [modalConfig, setModalConfig] = useState<IOpsMetricDetailConfig | null>(
    null
  );

  const periodLabel = PERIOD_LABELS[periodValue] || periodValue;
  const locationScope = selectedRegionName
    ? `${selectedRegionName} locations`
    : 'All locations';

  // Units per day average
  const actualPerDay = daysInPeriod > 0 ? unitsCollected / daysInPeriod : 0;
  const unitsPerDay = daysInPeriod > 0 ? actualPerDay.toFixed(1) : '0.0';

  // Pace ratio for color coding
  const paceRatio = expectedPerDay > 0 ? actualPerDay / expectedPerDay : 0;

  // Color-code: < .80 pink, .80-.95 yellow, >= .95 green
  const paceClass =
    paceRatio >= 0.95
      ? styles.statusUp
      : paceRatio >= 0.8
      ? styles.statusWarn
      : styles.statusDown;

  // SGL % of total donors (based on approvedDonationML threshold)
  const totalSglDbl = sglTotal + dblTotal;
  const sglPrecise = totalSglDbl > 0 ? (sglTotal / totalSglDbl) * 100 : 0;
  const sglPercent = Math.round(sglPrecise);

  // Color-code: >= 28% pink, 23-27.9% yellow, < 23% green
  const sglClass =
    sglPrecise >= 28
      ? styles.statusDown
      : sglPrecise >= 23
      ? styles.statusWarn
      : styles.statusUp;

  // Units/day trend
  const unitsPerDayTrendUp =
    prevUnitsPerDay !== undefined ? actualPerDay >= prevUnitsPerDay : undefined;
  const unitsPerDayTrendPct =
    prevUnitsPerDay !== undefined && prevUnitsPerDay > 0
      ? ((actualPerDay - prevUnitsPerDay) / prevUnitsPerDay) * 100
      : undefined;

  // Active donor growth %
  const growthPct =
    historicalActiveDonorCount !== undefined &&
    comparedPeriodActiveDonorCount !== undefined &&
    comparedPeriodActiveDonorCount > 0
      ? ((historicalActiveDonorCount - comparedPeriodActiveDonorCount) /
          comparedPeriodActiveDonorCount) *
        100
      : undefined;

  const openModal = (config: IOpsMetricDetailConfig, e: React.MouseEvent) => {
    e.stopPropagation();
    setModalConfig(config);
  };

  if (loading) {
    return (
      <div className={styles.summaryCardsRow}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className={styles.summaryCard}>
            <span className={styles.summaryCardLabel}>&nbsp;</span>
            <span className={styles.summaryCardValue}>&mdash;</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className={styles.summaryCardsRow}>
        {/* Current Active Donors */}
        <div className={styles.summaryCard}>
          <div
            className={styles.summaryCardInfoTab}
            onClick={(e) =>
              openModal(
                {
                  title: 'Current Active Donors',
                  description: (
                    <>
                      <strong>Current Active Donors</strong> is the real-time
                      count of donors across {locationScope} who are currently
                      in Active status. This is the primary capacity metric — it
                      directly determines how many donations can be scheduled
                      and collected at any given time.
                    </>
                  ),
                  rows: [
                    {
                      label: 'Active Donors (now)',
                      value: metricsLoading
                        ? '—'
                        : activeDonorCount.toLocaleString(),
                      bold: true,
                      color: '#4B9EFF',
                    },
                    { label: 'Scope', value: locationScope },
                  ],
                  formula: 'COUNT(donors WHERE status = Active)',
                },
                e
              )
            }
          >
            <InfoCircleOutlined />
          </div>
          <span className={styles.summaryCardLabel}>Current Active Donors</span>
          <span className={styles.summaryCardValue}>
            {metricsLoading ? '—' : activeDonorCount.toLocaleString()}
          </span>
          <span className={styles.summaryCardSub}>
            {selectedRegionName
              ? `All ${selectedRegionName} locations`
              : 'Across all locations'}
          </span>
        </div>

        {/* Active Donors (Period) */}
        <div className={styles.summaryCard}>
          <div
            className={styles.summaryCardInfoTab}
            onClick={(e) =>
              openModal(
                {
                  title: `Active Donors (${periodLabel})`,
                  description: (
                    <>
                      <strong>Active Donors ({periodLabel})</strong> compares
                      the active donor count at the end of the selected period
                      against the end of the prior equivalent period. The
                      percentage change reflects net growth or decline in donor
                      capacity over time. &apos;Active donor&apos; statuses are
                      configured in the Operations Dashboard Settings.
                    </>
                  ),
                  rows: [
                    {
                      label: `End of ${periodLabel}`,
                      value:
                        historicalActiveDonorCount !== undefined
                          ? historicalActiveDonorCount.toLocaleString()
                          : '—',
                      bold: true,
                      color: '#4B9EFF',
                    },
                    {
                      label: 'End of prior period',
                      value:
                        comparedPeriodActiveDonorCount !== undefined
                          ? comparedPeriodActiveDonorCount.toLocaleString()
                          : '—',
                    },
                    ...(growthPct !== undefined
                      ? [
                          {
                            label: 'Period-over-period change',
                            value: `${
                              growthPct > 0 ? '+' : ''
                            }${growthPct.toFixed(1)}%`,
                            bold: true,
                            color:
                              growthPct > 0
                                ? 'var(--status-up)'
                                : 'var(--status-down)',
                          },
                        ]
                      : []),
                  ],
                  formula:
                    'Growth % = (End Period Count − Prior Period Count) ÷ Prior Period Count × 100',
                },
                e
              )
            }
          >
            <InfoCircleOutlined />
          </div>
          <span className={styles.summaryCardLabel}>
            Active Donors ({periodLabel})
          </span>
          {metricsLoading ? (
            <span className={styles.summaryCardValue}>&mdash;</span>
          ) : (
            <div className={styles.activeDonorValueRow}>
              <div className={styles.activeDonorHistStack}>
                <span className={styles.activeDonorHistCount}>
                  {historicalActiveDonorCount !== undefined
                    ? historicalActiveDonorCount.toLocaleString()
                    : '—'}
                </span>
                <span className={styles.activeDonorHistLabel}>
                  as of end ({periodLabel})
                </span>
              </div>
              <div className={styles.activeDonorHistStack}>
                <span className={styles.activeDonorHistCount}>
                  {comparedPeriodActiveDonorCount !== undefined
                    ? comparedPeriodActiveDonorCount.toLocaleString()
                    : '—'}
                </span>
                <span className={styles.activeDonorHistLabel}>
                  as of end prev. period
                </span>
              </div>
              {historicalActiveDonorCount !== undefined &&
                comparedPeriodActiveDonorCount !== undefined &&
                comparedPeriodActiveDonorCount > 0 &&
                (() => {
                  const gPct =
                    ((historicalActiveDonorCount -
                      comparedPeriodActiveDonorCount) /
                      comparedPeriodActiveDonorCount) *
                    100;
                  if (gPct === 0) return null;
                  const rounded = Math.round(gPct);
                  const up = gPct > 0;
                  const pctLabel =
                    Math.abs(rounded) < 1 ? '<1' : `${Math.abs(rounded)}`;
                  const colorStyle = (() => {
                    if (!activeDonorGrowthGoal) return undefined;
                    const status = computeFlagStatus(
                      gPct,
                      activeDonorGrowthGoal,
                      false
                    );
                    if (status === 'up') return 'var(--status-up)';
                    if (status === 'warn') return 'var(--status-warn)';
                    return 'var(--status-down)';
                  })();
                  return (
                    <span
                      className={styles.activeDonorHistCount}
                      style={{ color: colorStyle }}
                    >
                      {up ? <ArrowUpOutlined /> : <ArrowDownOutlined />}{' '}
                      {pctLabel}%
                    </span>
                  );
                })()}
            </div>
          )}
        </div>

        {/* Recruited (Period) */}
        <div className={styles.summaryCard}>
          <div
            className={styles.summaryCardInfoTab}
            onClick={(e) =>
              openModal(
                {
                  title: `Recruited (${periodLabel})`,
                  description: (
                    <>
                      <strong>Recruited ({periodLabel})</strong> counts new
                      donor registrations across {locationScope} during the
                      selected period. Recruiting directly feeds the active
                      donor pipeline — without consistent recruiting, attrition
                      from retirements will gradually reduce capacity.
                    </>
                  ),
                  rows: [
                    {
                      label: `Recruited (${periodLabel})`,
                      value: recruitedCount.toLocaleString(),
                      bold: true,
                      color: '#E8008A',
                    },
                    {
                      label: 'Prior period',
                      value:
                        prevRecruitedCount !== undefined
                          ? prevRecruitedCount.toLocaleString()
                          : '—',
                    },
                    ...(prevRecruitedCount !== undefined &&
                    prevRecruitedCount > 0
                      ? (() => {
                          const pct =
                            ((recruitedCount - prevRecruitedCount) /
                              prevRecruitedCount) *
                            100;
                          return [
                            {
                              label: 'Period-over-period',
                              value: `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`,
                              bold: true,
                              color:
                                pct >= 0
                                  ? 'var(--status-up)'
                                  : 'var(--status-down)',
                            },
                          ];
                        })()
                      : []),
                    { label: 'Scope', value: locationScope },
                  ],
                  formula: `COUNT(donors WHERE registration_date WITHIN ${periodLabel} period)`,
                },
                e
              )
            }
          >
            <InfoCircleOutlined />
          </div>
          <span className={styles.summaryCardLabel}>
            Recruited ({periodLabel})
          </span>
          <div className={styles.healthFlagCardValueRow}>
            <span className={styles.summaryCardValue}>
              {recruitedCount.toLocaleString()}
            </span>
            <button
              type="button"
              className={styles.healthFlagDrilldownBtn}
              data-drilldown-id="summary:recruited"
              title="View in Drilldown"
              onClick={() => onDrilldown?.('summary:recruited')}
            >
              <TableOutlined />
            </button>
          </div>
          <span className={styles.summaryCardSub}>
            {prevRecruitedCount !== undefined
              ? prevRecruitedCount.toLocaleString()
              : '—'}{' '}
            Last Period{' '}
            <TrendIndicator
              current={recruitedCount}
              previous={prevRecruitedCount}
            />
          </span>
          <span className={styles.summaryCardSub}>{locationScope}</span>
        </div>

        {/* Units / Day Avg */}
        <div className={`${styles.summaryCard} ${paceClass}`}>
          <div
            className={styles.summaryCardInfoTab}
            onClick={(e) =>
              openModal(
                {
                  title: 'Units / Day Avg',
                  description: (
                    <>
                      <strong>Units / Day Avg</strong> is the average number of
                      pRBC units collected per calendar day during the selected
                      period across {locationScope}. It is compared against the
                      expected daily pace to give a real-time performance signal
                      — green means on track, yellow is approaching threshold,
                      red is below target.
                    </>
                  ),
                  rows: [
                    {
                      label: 'Actual units / day',
                      value: unitsPerDay,
                      bold: true,
                      color:
                        paceRatio >= 0.95
                          ? 'var(--status-up)'
                          : paceRatio >= 0.8
                          ? 'var(--status-warn)'
                          : 'var(--status-down)',
                    },
                    { label: 'Expected units / day', value: expectedPerDay },
                    {
                      label: 'Pace ratio',
                      value: `${Math.round(paceRatio * 100)}%`,
                      color:
                        paceRatio >= 0.95
                          ? 'var(--status-up)'
                          : paceRatio >= 0.8
                          ? 'var(--status-warn)'
                          : 'var(--status-down)',
                    },
                    { label: 'Total units collected', value: unitsCollected },
                    { label: 'Days in period', value: daysInPeriod },
                    ...(prevUnitsPerDay !== undefined
                      ? [
                          {
                            label: 'Prior period units / day',
                            value: prevUnitsPerDay.toFixed(1),
                          },
                          ...(prevUnitsCollected !== undefined
                            ? [
                                {
                                  label: 'Prior period units collected',
                                  value: prevUnitsCollected,
                                },
                              ]
                            : []),
                          ...(prevDaysInPeriod !== undefined
                            ? [
                                {
                                  label: 'Prior period days',
                                  value: prevDaysInPeriod,
                                },
                              ]
                            : []),
                          ...(unitsPerDayTrendPct !== undefined
                            ? [
                                {
                                  label: 'Period-over-period',
                                  value: `${
                                    unitsPerDayTrendPct > 0 ? '+' : ''
                                  }${unitsPerDayTrendPct.toFixed(1)}%`,
                                  bold: true,
                                  color: unitsPerDayTrendUp
                                    ? 'var(--status-up)'
                                    : 'var(--status-down)',
                                },
                              ]
                            : []),
                        ]
                      : []),
                  ],
                  formula: 'Units/Day = Total Units Collected ÷ Days in Period',
                },
                e
              )
            }
          >
            <InfoCircleOutlined />
          </div>
          <span className={styles.summaryCardLabel}>Units / Day Avg</span>
          <div className={styles.healthFlagCardValueRow}>
            <span className={`${styles.summaryCardValue} ${paceClass}`}>
              {unitsPerDay}
            </span>
            <button
              type="button"
              className={styles.healthFlagDrilldownBtn}
              data-drilldown-id="summary:units"
              title="View units by location"
              onClick={() => onDrilldown?.('summary:units')}
            >
              <TableOutlined />
            </button>
          </div>
          <span className={styles.summaryCardSub}>
            {expectedPerDay} expected{' '}
            {unitsPerDayTrendPct !== undefined && unitsPerDayTrendPct !== 0 && (
              <span
                className={
                  unitsPerDayTrendUp ? styles.statusUp : styles.statusDown
                }
              >
                {unitsPerDayTrendUp ? (
                  <ArrowUpOutlined />
                ) : (
                  <ArrowDownOutlined />
                )}{' '}
                {Math.abs(unitsPerDayTrendPct).toFixed(1)}%
              </span>
            )}
          </span>
          <span className={styles.summaryCardSub}>{locationScope}</span>
        </div>

        {/* Success Rate */}
        <div className={styles.summaryCard}>
          <div
            className={styles.summaryCardInfoTab}
            onClick={(e) =>
              openModal(
                {
                  title: 'Success Rate',
                  description: (
                    <>
                      <strong>Success Rate</strong> is the percentage of
                      scheduled donation attempts across {locationScope} that
                      resulted in a successful collection during the period. A
                      low success rate indicates high donation failure and/or
                      processing failure rates and reduces effective output even
                      when donor count is healthy.
                    </>
                  ),
                  rows: [
                    {
                      label: 'Success Rate',
                      value:
                        successRate != null
                          ? `${successRate.toFixed(1)}%`
                          : '—',
                      bold: true,
                      color:
                        successRate == null
                          ? undefined
                          : successRate >= 80
                          ? 'var(--status-up)'
                          : successRate >= 65
                          ? 'var(--status-warn)'
                          : 'var(--status-down)',
                    },
                    {
                      label: 'Prior period',
                      value:
                        prevSuccessRate !== undefined
                          ? `${prevSuccessRate.toFixed(1)}%`
                          : '—',
                    },
                    ...(successRate != null && prevSuccessRate !== undefined
                      ? (() => {
                          const diff =
                            Math.round(successRate * 10) / 10 -
                            Math.round(prevSuccessRate * 10) / 10;
                          const sign = diff > 0 ? '+' : '';
                          return [
                            {
                              label: 'Period-over-period',
                              value: `${sign}${diff.toFixed(1)}%`,
                              bold: true,
                              color:
                                diff >= 0
                                  ? 'var(--status-up)'
                                  : 'var(--status-down)',
                            },
                          ];
                        })()
                      : []),
                    { label: 'Scope', value: locationScope },
                  ],
                  formula:
                    'Success Rate = Successful Donations ÷ Total Attempts × 100',
                },
                e
              )
            }
          >
            <InfoCircleOutlined />
          </div>
          <span className={styles.summaryCardLabel}>Success Rate</span>
          <span className={styles.summaryCardValue}>
            {successRate != null ? `${Math.round(successRate)}%` : '—'}
          </span>
          <span className={styles.summaryCardSub}>
            {prevSuccessRate !== undefined
              ? `${Math.round(prevSuccessRate)}%`
              : '—'}{' '}
            Last Period{' '}
            {successRate != null && (
              <TrendIndicator
                current={successRate}
                previous={prevSuccessRate}
                mode="absolute"
              />
            )}
          </span>
          <span className={styles.summaryCardSub}>{locationScope}</span>
        </div>

        {/* SGL% of Total */}
        <div className={`${styles.summaryCard} ${sglClass}`}>
          <div
            className={styles.summaryCardInfoTab}
            onClick={(e) =>
              openModal(
                {
                  title: 'SGL% of Total',
                  description: (
                    <>
                      <strong>SGL% of Total</strong> measures the proportion of
                      active donors who donate a single pRBC unit per session
                      versus double unit (DBL) donors. Because DBL donors
                      produce twice the output, a high SGL% significantly
                      reduces overall collection efficiency. Target is to keep
                      SGL below 23%.
                    </>
                  ),
                  rows: [
                    {
                      label: 'SGL%',
                      value: `${sglPercent}%`,
                      bold: true,
                      color:
                        sglPrecise >= 28
                          ? 'var(--status-down)'
                          : sglPrecise >= 23
                          ? 'var(--status-warn)'
                          : 'var(--status-up)',
                    },
                    { label: 'SGL donors', value: sglTotal, indent: true },
                    { label: 'DBL donors', value: dblTotal, indent: true },
                    { label: 'Total donors', value: totalSglDbl },
                    { label: 'Target', value: '< 23%' },
                  ],
                  formula: 'SGL% = SGL Donors ÷ (SGL + DBL Donors) × 100',
                },
                e
              )
            }
          >
            <InfoCircleOutlined />
          </div>
          <span className={styles.summaryCardLabel}>SGL% of Total</span>
          <span className={`${styles.summaryCardValue} ${sglClass}`}>
            {sglPercent}%
          </span>
          <div className={styles.sglBar}>
            <div
              className={`${styles.sglBarFill} ${sglClass}`}
              style={{ width: `${sglPercent}%` }}
            />
            <span
              className={styles.sglBarMarker}
              style={{ left: '25%' }}
              title="25% target"
            >
              <span className={styles.sglBarMarkerLabel}>25%</span>
            </span>
          </div>
          <span className={styles.summaryCardSub}>
            SGL {sglTotal} / DBL {dblTotal}
          </span>
          <span className={styles.summaryCardSub}>{locationScope}</span>
        </div>
      </div>

      <OpsMetricDetailModal
        visible={modalConfig !== null}
        onClose={() => setModalConfig(null)}
        config={modalConfig}
        isDarkMode={isDarkMode}
      />
    </>
  );
};

export default OpsRecruiterSummaryCards;
